// Вход в админку ключом доступа — сторона браузера (26 сен 2026, спека
// docs/superpowers/specs/2026-09-26-admin-passkey-access-verification.md, пункт 4).
//
// Три церемонии, у каждой два шага:
//   • подготовить — взять у шлюза вызов (challenge) и параметры ключа;
//   • провести   — открыть окно ключа (отпечаток / лицо / PIN-код / телефон по QR-коду) и отдать
//     ответ шлюзу; шлюз ставит куку живой сессии сам (HttpOnly — странице её не видно).
//
// ПОЧЕМУ ШАГОВ ДВА. Safari открывает окно ключа только в ответ на нажатие и только пока нажатие
// «свежее»: сетевой запрос между нажатием и окном может его погасить — окно не откроется вовсе.
// Поэтому вызов берётся заранее (при показе экрана), а нажатие сразу открывает окно. Вызов живёт
// у шлюза 5 минут; подготовка старше `PREPARED_MAX_AGE_MS` перед нажатием берётся заново.
//
// Исходы — не тексты: что показать человеку, решает экран (`admin-copy.ts`).

import {
  browserSupportsWebAuthn,
  sendSignal,
  startAuthentication,
  startRegistration,
  WebAuthnError,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { ADMIN_HOST } from '@padla/core/admin-access/admin-origin';

export type CeremonyFailure =
  /** Человек закрыл окно ключа или не успел (браузер не различает). */
  | 'cancelled'
  /** Браузер не умеет ключи доступа. */
  | 'unsupported'
  /** Страница открыта не на panel.padla.pro — ключ, привязанный к нему, здесь не покажется. */
  | 'wrong_host'
  /** Привязка: на этом устройстве ключ этой админки уже есть. */
  | 'already_here'
  /** Ключ не умеет проверять человека (палец, лицо, PIN-код) — такой не принимаем. */
  | 'weak_key'
  /** Шлюз не знает этот ключ: отозван или от другой учётки. */
  | 'unknown_key'
  /** Ссылка из бота использована или истекла. */
  | 'link_dead'
  /** Добавить ключ можно только сразу после входа — нужно подтвердить вход ключом. */
  | 'fresh_login_required'
  /** Живой сессии больше нет: вышли на другом устройстве, «не я» или срок. */
  | 'session_ended'
  /** Вызов истёк, пока было открыто окно, — нажать ещё раз. */
  | 'expired'
  | 'rate_limited'
  /** Запрос пришёл через российский мост — админки через него нет. */
  | 'via_bridge'
  /** Шлюз отверг ответ ключа (подпись, чужой адрес страницы, счётчик назад). */
  | 'rejected'
  /** Устройство не смогло создать или прочитать ключ. */
  | 'device_error'
  /** Сеть или шлюз не ответили. */
  | 'unavailable';

export type CeremonyResult<T> = { ok: true; value: T } | { ok: false; failure: CeremonyFailure };

export interface AdminAccountView {
  displayName: string;
  role: 'admin' | 'superadmin';
}

/** Вызов, взятый заранее. `at` — когда (для проверки свежести перед нажатием). */
export interface Prepared<O> {
  options: O;
  at: number;
}

/** Вызов живёт у шлюза 5 минут; берём заново, если подготовка старше 4. */
export const PREPARED_MAX_AGE_MS = 4 * 60_000;

export function isFresh(p: Prepared<unknown> | null, now = Date.now()): boolean {
  return p !== null && now - p.at < PREPARED_MAX_AGE_MS;
}

/** Можно ли здесь вообще входить ключом (до всякой сети). */
export function passkeyAvailability(
  hostname = globalThis.location?.hostname ?? ''
): 'ok' | 'wrong_host' | 'unsupported' {
  if (hostname !== ADMIN_HOST) return 'wrong_host';
  if (!browserSupportsWebAuthn()) return 'unsupported';
  return 'ok';
}

interface GatewayReply {
  status: number | null;
  data: Record<string, unknown> | null;
}

async function call(
  apiBase: string,
  path: string,
  method: 'GET' | 'POST' | 'DELETE',
  body?: unknown
): Promise<GatewayReply> {
  try {
    const res = await fetch(`${apiBase}/v1/admin${path}`, {
      method,
      // Кука живой сессии — того же адреса: браузер несёт её сам (credentials по умолчанию).
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    let data: Record<string, unknown> | null = null;
    try {
      data = (await res.json()) as Record<string, unknown>;
    } catch {
      data = null;
    }
    return { status: res.status, data };
  } catch {
    return { status: null, data: null };
  }
}

/** Ответ шлюза-отказ → исход. */
export function failureOfReply(r: GatewayReply): CeremonyFailure {
  const error = typeof r.data?.error === 'string' ? r.data.error : '';
  if (r.status === null || r.status >= 500) return 'unavailable';
  if (r.status === 429) return 'rate_limited';
  if (r.status === 410 || error === 'link_dead') return 'link_dead';
  if (error === 'via_bridge') return 'via_bridge';
  if (error === 'fresh_login_required') return 'fresh_login_required';
  if (error === 'unknown_key') return 'unknown_key';
  if (error === 'challenge') return 'expired';
  if (r.status === 401 && (error === 'no_session' || error === 'session_dead'))
    return 'session_ended';
  return 'rejected';
}

/** Ошибка окна ключа (браузер) → исход. */
export function failureOfBrowser(err: unknown): CeremonyFailure {
  if (err instanceof WebAuthnError) {
    switch (err.code) {
      case 'ERROR_CEREMONY_ABORTED':
        return 'cancelled';
      case 'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY':
        // NotAllowedError: окно закрыто или время вышло — браузер нарочно не различает.
        return (err.cause as { name?: string } | undefined)?.name === 'NotAllowedError'
          ? 'cancelled'
          : 'device_error';
      case 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED':
        return 'already_here';
      case 'ERROR_INVALID_DOMAIN':
      case 'ERROR_INVALID_RP_ID':
        return 'wrong_host';
      case 'ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT':
      case 'ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT':
        return 'weak_key';
      default:
        return 'device_error';
    }
  }
  // Поддержку браузера экран проверяет до нажатия (`passkeyAvailability`), поэтому прочие ошибки —
  // сбой устройства; окно, закрытое мимо обёртки библиотеки, — всё равно отмена.
  if ((err as { name?: string } | null)?.name === 'NotAllowedError') return 'cancelled';
  return 'device_error';
}

function accountOf(data: Record<string, unknown> | null): AdminAccountView {
  const a = (data?.account ?? {}) as Partial<AdminAccountView>;
  return {
    displayName: String(a.displayName ?? ''),
    role: a.role === 'superadmin' ? 'superadmin' : 'admin',
  };
}

// ── вход ──────────────────────────────────────────────────────────────────────

export async function prepareLogin(
  apiBase: string
): Promise<CeremonyResult<Prepared<PublicKeyCredentialRequestOptionsJSON>>> {
  const r = await call(apiBase, '/login/options', 'POST', {});
  if (r.status !== 200 || !r.data?.options) return { ok: false, failure: failureOfReply(r) };
  return {
    ok: true,
    value: { options: r.data.options as PublicKeyCredentialRequestOptionsJSON, at: Date.now() },
  };
}

export async function runLogin(
  apiBase: string,
  prepared: Prepared<PublicKeyCredentialRequestOptionsJSON>
): Promise<CeremonyResult<AdminAccountView>> {
  let response;
  try {
    response = await startAuthentication({ optionsJSON: prepared.options });
  } catch (err) {
    return { ok: false, failure: failureOfBrowser(err) };
  }
  const r = await call(apiBase, '/login/verify', 'POST', { response });
  if (r.status === 200 && r.data?.ok === true) return { ok: true, value: accountOf(r.data) };
  const failure = failureOfReply(r);
  if (failure === 'unknown_key') {
    // Просим браузер спрятать ключ, которого шлюз больше не знает (отозван): иначе окно входа
    // будет и дальше его предлагать. Не все браузеры умеют — тогда просто ничего не случится.
    void sendSignal({
      signalName: 'unknownCredential',
      rpID: ADMIN_HOST,
      credentialID: response.id,
    }).catch(() => {});
  }
  return { ok: false, failure };
}

// ── первый ключ по ссылке из бота ─────────────────────────────────────────────

export interface EnrollPrepared extends Prepared<PublicKeyCredentialCreationOptionsJSON> {
  account: AdminAccountView;
}

export async function prepareEnroll(
  apiBase: string,
  token: string
): Promise<CeremonyResult<EnrollPrepared>> {
  const r = await call(apiBase, '/enroll/options', 'POST', { token });
  if (r.status !== 200 || !r.data?.options) return { ok: false, failure: failureOfReply(r) };
  return {
    ok: true,
    value: {
      options: r.data.options as PublicKeyCredentialCreationOptionsJSON,
      account: accountOf(r.data),
      at: Date.now(),
    },
  };
}

export async function runEnroll(
  apiBase: string,
  token: string,
  prepared: EnrollPrepared
): Promise<CeremonyResult<AdminAccountView>> {
  let response;
  try {
    response = await startRegistration({ optionsJSON: prepared.options });
  } catch (err) {
    return { ok: false, failure: failureOfBrowser(err) };
  }
  const r = await call(apiBase, '/enroll/verify', 'POST', { token, response });
  if (r.status === 200 && r.data?.ok === true) return { ok: true, value: accountOf(r.data) };
  return { ok: false, failure: failureOfReply(r) };
}

// ── ещё один ключ из живой сессии ─────────────────────────────────────────────

/** Где создать ключ: на телефоне (браузер сразу покажет QR-код) или на этом устройстве. */
export type KeyPlace = 'phone' | 'this_device';

export async function prepareAddKey(
  apiBase: string
): Promise<CeremonyResult<Prepared<PublicKeyCredentialCreationOptionsJSON>>> {
  const r = await call(apiBase, '/passkeys/options', 'POST', {});
  if (r.status !== 200 || !r.data?.options) return { ok: false, failure: failureOfReply(r) };
  return {
    ok: true,
    value: { options: r.data.options as PublicKeyCredentialCreationOptionsJSON, at: Date.now() },
  };
}

export async function runAddKey(
  apiBase: string,
  prepared: Prepared<PublicKeyCredentialCreationOptionsJSON>,
  place: KeyPlace
): Promise<CeremonyResult<true>> {
  // Подсказка браузеру, какое окно открыть первым (WebAuthn L3 `hints`): на безопасность не влияет,
  // браузер без поддержки её просто не читает.
  const optionsJSON: PublicKeyCredentialCreationOptionsJSON = {
    ...prepared.options,
    hints: place === 'phone' ? ['hybrid'] : ['client-device'],
  };
  let response;
  try {
    response = await startRegistration({ optionsJSON });
  } catch (err) {
    return { ok: false, failure: failureOfBrowser(err) };
  }
  const r = await call(apiBase, '/passkeys/verify', 'POST', { response });
  if (r.status === 200 && r.data?.ok === true) return { ok: true, value: true };
  return { ok: false, failure: failureOfReply(r) };
}

// ── сессия, ключи, входы ──────────────────────────────────────────────────────

export interface MeView {
  account: AdminAccountView;
  session: { id: string; createdAt: string; expiresAt: string; fresh: boolean };
}

export interface PasskeyView {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
  synced: boolean;
  current: boolean;
}

export interface SessionView {
  id: string;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  ip: string | null;
  device: string;
  current: boolean;
}

export async function fetchMe(apiBase: string): Promise<CeremonyResult<MeView>> {
  const r = await call(apiBase, '/me', 'GET');
  if (r.status === 200 && r.data?.ok === true) {
    return {
      ok: true,
      value: { account: accountOf(r.data), session: r.data.session as MeView['session'] },
    };
  }
  return { ok: false, failure: failureOfReply(r) };
}

export async function fetchPasskeys(apiBase: string): Promise<CeremonyResult<PasskeyView[]>> {
  const r = await call(apiBase, '/passkeys', 'GET');
  if (r.status === 200 && Array.isArray(r.data?.passkeys))
    return { ok: true, value: r.data.passkeys as PasskeyView[] };
  return { ok: false, failure: failureOfReply(r) };
}

export async function fetchSessions(apiBase: string): Promise<CeremonyResult<SessionView[]>> {
  const r = await call(apiBase, '/sessions', 'GET');
  if (r.status === 200 && Array.isArray(r.data?.sessions))
    return { ok: true, value: r.data.sessions as SessionView[] };
  return { ok: false, failure: failureOfReply(r) };
}

/** Ответ: закрыт ли этим действием вход на этом устройстве. */
export async function revokePasskey(
  apiBase: string,
  id: string
): Promise<CeremonyResult<{ loggedOut: boolean }>> {
  const r = await call(apiBase, `/passkeys/${encodeURIComponent(id)}`, 'DELETE');
  if (r.status === 200 && r.data?.ok === true)
    return { ok: true, value: { loggedOut: r.data.loggedOut === true } };
  return { ok: false, failure: failureOfReply(r) };
}

export async function endSession(
  apiBase: string,
  id: string
): Promise<CeremonyResult<{ loggedOut: boolean }>> {
  const r = await call(apiBase, `/sessions/${encodeURIComponent(id)}`, 'DELETE');
  if (r.status === 200 && r.data?.ok === true)
    return { ok: true, value: { loggedOut: r.data.loggedOut === true } };
  return { ok: false, failure: failureOfReply(r) };
}

export async function logoutEverywhere(
  apiBase: string
): Promise<CeremonyResult<{ count: number }>> {
  const r = await call(apiBase, '/logout-all', 'POST', {});
  if (r.status === 200 && r.data?.ok === true)
    return { ok: true, value: { count: Number(r.data.count ?? 0) } };
  return { ok: false, failure: failureOfReply(r) };
}

export async function logoutHere(apiBase: string): Promise<void> {
  await call(apiBase, '/logout', 'POST', {});
}
